import React, {
    useCallback, useMemo, useState,
} from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    LexicalTypeaheadMenuPlugin,
    useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { TextNode } from 'lexical';

import { createPortal } from 'react-dom';
import useDebounce from 'hooks/useDebounce';
import {
    DefaultError, useInfiniteQuery, UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import NSLoadingScreen from 'bricks/NSLoadingScreen/NSLoadingScreen';
import { $createMentionNode } from './MentionNode';
import { MentionPluginItem, MentionTypeaheadOption } from './types';
import { MentionsTypeaheadMenuItem } from './components/MentionsTypeaheadMenuItem';
import {
    SUGGESTION_LIST_LENGTH_LIMIT,
} from './constants';
import { getPossibleQueryMatch } from './helpers';

export interface MentionsPluginProps {
    mentionsQueryOptions: (query: string) => UseInfiniteQueryOptions<any, DefaultError, MentionPluginItem[], any, any, any>;
    onSelect: (option: MentionPluginItem) => void;
}

const MentionsPlugin = ({ onSelect, mentionsQueryOptions }: MentionsPluginProps) => {
    const [editor] = useLexicalComposerContext();
    const [queryString, setQueryString] = useState<string | null>(null);

    // Storing this in state as opposed to deriving it so that while the query
    // is loading, we can persist whether the previous query was empty or not.
    const [areResultsEmpty, setAreResultsEmpty] = useState(false);

    const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch('/', {
        minLength: 0,
    });

    const debouncedQueryString = useDebounce(queryString, 300);
    const { data: results, isSuccess, isPending } = useInfiniteQuery(
        mentionsQueryOptions(debouncedQueryString || ''),
    );

    if (isSuccess && results.length === 0 && !areResultsEmpty) {
        // Stop showing the menu once we receive a
        // successful response with no results
        setAreResultsEmpty(true);
    }
    if (isSuccess && results.length > 0 && areResultsEmpty) {
        // Show the menu if we start receiving results again
        setAreResultsEmpty(false);
    }

    const options = useMemo(
        () => results
            ?.map(result => new MentionTypeaheadOption(result.name, result.type, result.id, result.picture))
            ?.slice(0, SUGGESTION_LIST_LENGTH_LIMIT) || [],
        [results],
    );

    const onSelectOption = useCallback(
        (
            selectedOption: MentionTypeaheadOption,
            nodeToReplace: TextNode | null,
            closeMenu: () => void,
        ) => {
            editor.update(() => {
                const mentionNode = $createMentionNode(selectedOption.name);
                if (nodeToReplace) {
                    nodeToReplace.replace(mentionNode);
                }
                mentionNode.select();
                closeMenu();
            });
            onSelect(selectedOption);
        },
        [editor, onSelect],
    );

    const checkForMentionMatch = useCallback(
        (text: string) => {
            const slashMatch = checkForSlashTriggerMatch(text, editor);
            if (slashMatch !== null) {
                return null;
            }
            return getPossibleQueryMatch(text);
        },
        [checkForSlashTriggerMatch, editor],
    );

    return (
        <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
            onQueryChange={setQueryString}
            onSelectOption={onSelectOption}
            triggerFn={checkForMentionMatch}
            options={options}
            menuRenderFn={(
                anchorElementRef,
                { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
            ) => (anchorElementRef.current && !areResultsEmpty
                ? createPortal(
                    <div className="NSRichTextEditor__mention-menu-container">
                        <ul className="NSRichTextEditor__mention-menu-list">
                            {isPending ? (
                                <li
                                    className="NSRichTextEditor__mention-menu"
                                >
                                    <NSLoadingScreen />
                                </li>
                            ) : options.map((option, i: number) => (
                                <MentionsTypeaheadMenuItem
                                    index={i}
                                    isSelected={selectedIndex === i}
                                    onClick={() => {
                                        setHighlightedIndex(i);
                                        selectOptionAndCleanUp(option);
                                    }}
                                    onMouseEnter={() => {
                                        setHighlightedIndex(i);
                                    }}
                                    key={`${option.id}-${option.name}`}
                                    option={option}
                                />
                            ))}
                        </ul>
                    </div>,
                    anchorElementRef.current,
                )
                : null)}
        />
    );
};

export default MentionsPlugin;
